// @ts-nocheck
(async function () {
  const container = document.querySelector('[data-video-id]');
  if (!container) return;

  // The video facade is wired up regardless of whether this article has a transcript.
  // These lookups used to sit below the transcript setup, after three transcript-dependent
  // early returns, so an article with a video but no transcript file never got a click
  // handler and its play button did nothing at all.
  const videoWrap = container.querySelector('.article-media-transcript__video');
  const facade = container.querySelector('.article-video-facade');
  const videoId = container.dataset.videoId;
  if (!videoWrap || !facade || !videoId) return;

  // The transcript column is only rendered when the article has a transcript file, so
  // both of these are legitimately absent on video-only articles.
  const transcriptEl = document.getElementById('transcript-body');
  const transcriptUrl = container.dataset.transcriptUrl;
  const hasTranscript = Boolean(transcriptEl && transcriptUrl);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
  };

  // ── Parse VTT ────────────────────────────────────────────────
  function parseVtt(raw) {
    const out = [];
    const blocks = raw.replace(/^﻿/, '').split(/\r?\n\r?\n/);
    const timeRe = /(\d+):(\d+):(\d+)[.,](\d+)\s*-->\s*(\d+):(\d+):(\d+)[.,](\d+)/;
    for (const block of blocks) {
      const m = block.match(timeRe);
      if (!m) continue;
      const start =
        parseInt(m[1], 10) * 3600 +
        parseInt(m[2], 10) * 60 +
        parseInt(m[3], 10) +
        parseInt(m[4], 10) / 1000;
      // Text = everything after the timing line
      const text = block
        .split(/\r?\n/)
        .slice(block.split(/\r?\n/).findIndex((l) => timeRe.test(l)) + 1)
        .join(' ')
        .replace(/<[^>]+>/g, '') // strip inline tags like <c>, <v>
        .replace(/\s+/g, ' ')
        .trim();
      if (text) out.push({ start, text });
    }
    return out;
  }

  // Transcript lines stay empty on video-only articles, and on fetch/parse failure.
  // In all of those cases the video below still has to work, so none of this returns
  // out of the IIFE any more.
  let lines = [];
  if (hasTranscript) {
    let vttText;
    try {
      const res = await fetch(transcriptUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      vttText = await res.text();
    } catch (err) {
      console.warn('Transcript fetch failed:', err);
      transcriptEl.innerHTML = '<p>Transcript unavailable.</p>';
    }

    if (vttText) {
      lines = parseVtt(vttText);
      if (!lines.length) transcriptEl.innerHTML = '<p>Transcript not available.</p>';
    }
  }

  // ── Initial placeholder (transcript hidden until play) ────────
  if (lines.length) {
    transcriptEl.innerHTML = `
    <div class="transcript-placeholder">
      <svg class="transcript-placeholder__icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10 9 9 9 8 9"/>
      </svg>
      <p class="transcript-placeholder__title">Transcript available</p>
      <p class="transcript-placeholder__hint">Play the video to follow along.</p>
    </div>
  `;
  }

  // Sync transcript column height to the video box (facade now, iframe after play)
  const textCol = container.querySelector('.article-media-transcript__text');
  if (textCol && window.ResizeObserver) {
    const syncHeight = () => {
      if (window.matchMedia('(max-width: 768px)').matches) {
        textCol.style.height = '';
      } else {
        textCol.style.height = `${videoWrap.getBoundingClientRect().height}px`;
      }
    };
    const ro = new ResizeObserver(syncHeight);
    ro.observe(videoWrap);
    window.addEventListener('resize', syncHeight);
    syncHeight();
  }

  let lineEls = null;
  let revealed = false;
  let player = null;
  let building = false;

  function revealTranscript() {
    // Nothing to reveal on a video-only article, or if the transcript failed to load.
    if (revealed || !lines.length) return;
    revealed = true;
    transcriptEl.innerHTML = lines.map((l, i) => `
      <div class="transcript-line" data-index="${i}" data-start="${l.start}">
        <button type="button" class="transcript-timestamp" data-start="${l.start}">${formatTime(l.start)}</button>
        <span class="transcript-text">${l.text}</span>
      </div>
    `).join('');
    lineEls = transcriptEl.querySelectorAll('.transcript-line');

    transcriptEl.addEventListener('click', (e) => {
      const btn = e.target.closest('.transcript-timestamp');
      if (!btn) return;
      const start = parseFloat(btn.dataset.start);
      if (player && player.seekTo) {
        player.seekTo(start, true);
        player.playVideo();
      }
    });

    startSync();
  }

  // ── Load YT IFrame API once ───────────────────────────────────
  function loadYtApi() {
    return new Promise((resolve) => {
      if (window.YT && window.YT.Player) return resolve();
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof prev === 'function') prev();
        resolve();
      };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
    });
  }

  // ── Build the real player on first play-click ─────────────────
  async function buildPlayer() {
    if (building) return;
    building = true;

    const thumb = facade.querySelector('img');
    const iframe = document.createElement('iframe');
    iframe.id = 'shero-yt-player';
    iframe.className = 'article-video-embed';
    iframe.title = (thumb && thumb.alt) || document.title;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('frameborder', '0');
    iframe.src = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=1&origin=${encodeURIComponent(window.location.origin)}`;

    facade.replaceWith(iframe);

    await loadYtApi();

    player = new YT.Player(iframe.id, {
      events: {
        onStateChange: (e) => {
          if (e.data === 1) revealTranscript();
        },
      },
    });
  }

  facade.addEventListener('click', buildPlayer);

  // ── Sync loop ────────────────────────────────────────────────
  let activeIdx = -1;
  let userScrolling = false;
  let userScrollTimer;

  if (transcriptEl) {
    transcriptEl.addEventListener('scroll', () => {
      userScrolling = true;
      clearTimeout(userScrollTimer);
      userScrollTimer = setTimeout(() => { userScrolling = false; }, 1500);
    }, { passive: true });
  }

  function startSync() {
    setInterval(() => {
      if (!player.getCurrentTime || !lineEls) return;
      const t = player.getCurrentTime();
      let lo = 0, hi = lines.length - 1, idx = 0;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (lines[mid].start <= t) { idx = mid; lo = mid + 1; }
        else hi = mid - 1;
      }
      if (idx === activeIdx) return;
      if (activeIdx >= 0 && lineEls[activeIdx]) lineEls[activeIdx].classList.remove('transcript-line--active');
      activeIdx = idx;
      const el = lineEls[idx];
      el.classList.add('transcript-line--active');
      if (!userScrolling) {
        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        const target = isMobile
          ? el.offsetTop - transcriptEl.offsetTop - 8
          : el.offsetTop - transcriptEl.offsetTop
            - (transcriptEl.clientHeight / 2)
            + (el.clientHeight / 2);
        transcriptEl.scrollTo({ top: target, behavior: 'smooth' });
      }
    }, 300);
  }
})();
